'use client';

import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { useMicroSource, type MicroContentKind } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { MicroItemCard } from './_MicroItemCard';
import { microCardGridStyle } from './_cardGrid';
import { useMicroCardDecorations } from './_useMicroCardDecorations';

/**
 * Paginated grid of micro content. Reads {@code ?page=} +
 * {@code ?kind=} + {@code ?category=} from the URL.
 */
export interface MicroContentGridProps {
  title?: string;
  kind?: MicroContentKind;
  pageSize?: number;
  itemHrefTemplate?: string;
  /** Show the content-kind chip over each card's cover. */
  showKindBadge?: boolean;
}

function useUrlParam(name: string): string | undefined {
  // useSearchParams (not window.location) — identical value on the server
  // pass and the client's first render, so no hydration mismatch, and it
  // re-reads on client-side navigation.
  const params = useSearchParams();
  return params?.get(name) ?? undefined;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function MicroContentGrid({
  title,
  kind: defaultKind,
  pageSize: rawPageSize = 12,
  itemHrefTemplate,
  showKindBadge = true,
}: MicroContentGridProps) {
  const { t } = useTranslation('site-builder-blocks');
  const micro = useMicroSource();
  const decorations = useMicroCardDecorations();
  const pageSize = clamp(rawPageSize, 1, 50);
  const pageRaw = useUrlParam('page');
  const category = useUrlParam('category');
  const kindParam = useUrlParam('kind');
  const page = (() => {
    const p = parseInt(pageRaw ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();
  const kind = (kindParam as MicroContentKind | undefined) ?? defaultKind;

  const state = useAsyncResource(
    async (signal) => {
      if (!micro) return null;
      const args: Parameters<typeof micro.content.list>[0] = { page, pageSize, signal };
      if (kind) args.kind = kind;
      if (category) args.category = category;
      const res = await micro.content.list(args);
      return res.items.length > 0 ? res : null;
    },
    [micro, page, pageSize, kind, category],
  );

  if (!micro) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder blockType="MicroContentGrid" adapterName="MicroSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell title={title}>
        <SkeletonRow count={pageSize} layout="grid" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title}>
        <EmptyState
          title={t('microContentGrid.empty.title')}
          hint={t('microContentGrid.empty.hint')}
        />
      </SectionShell>
    );
  }

  return (
    <SectionShell title={title}>
      <div style={microCardGridStyle(kind)}>
        {state.data.items.map((item) => (
          <MicroItemCard
            showKindBadge={showKindBadge}
            cover={kind === 'SHORTS' ? 'portrait' : 'landscape'}
            key={item.id}
            item={item}
            progress={decorations.progressFor(item.id)}
            categoryName={decorations.categoryNameFor(item.categoryId)}
            {...(itemHrefTemplate ? { hrefTemplate: itemHrefTemplate } : {})}
          />
        ))}
      </div>
    </SectionShell>
  );
}
